<template>
  <div class="main-component-pc-lector-articulo-elaborado-y-actividad">
    <UCMultiInputSelector
      ref="multiInputSelectorRef"
      :conf="confUCMultiSelector"
      :allow-change="true"
      @change="handleInputChange"
      @validate-input="handleValidateInput"
      @code-scanner-close="codeScannerClose"
    />
  </div>
</template>

<script setup>
// @ts-check
import UCMultiInputSelector from '@/components/common/UCMultiInputSelector.vue';
import icCode from '@/static/icons/ic_code.svg';
import icPalletizing from '@/static/icons/ic_logistic_unit.svg';
import { sleep } from '@/js/utils/sleep';
import { useI18n } from 'vue-i18n';
import {
  computed,
  onMounted,
  ref,
  shallowRef,
} from 'vue';

/** @import { PropType, Ref, ShallowRef } from 'vue' */

const props = defineProps({
  // Permite que la camara continue abierta para hacer lecturas mientras no se pulsa el botón back.
  continuousBarCodeReading: {
    type: Boolean,
    default: false,
  },
  orientationScan: {
    type: /** @type {PropType<'portrait' | 'landscape'>} */ (String),
    default: 'portrait',
  },
  // Flash Camara
  torchOn: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'code-reading-elaborated-article',
  'code-reading-palet',
  'updateFront',
]);

const { t } = useI18n();
// Controla si se permite el cambio utilizando el botón de cambio de input.
const allowChangeComponentUCMultiInputSelector = ref(false);
/** @type {ShallowRef<InstanceType<typeof UCMultiInputSelector> | null>} */
const multiInputSelectorRef = shallowRef(null);

const confUCMultiSelector = computed(() => [
  /** @type {const} */ ({
    id: 'PALET',
    placeholder: t('production.common.palletReading'),
    iconButton: icPalletizing,
    altButton: 'Icono lectura de palé',
    showBarCodeButton: true,
  }),
  /** @type {const} */ ({
    id: 'BOX',
    placeholder: t('production.common.boxReading'),
    iconButton: icCode,
    altButton: 'Icono lectura de cajas',
    showBarCodeButton: true,
    confReadBarCode: {
      fabButtonShow: false,
      autoOpenCamera: true,
      autofocusOnInput: false,
      continuousBarCodeReading: props.continuousBarCodeReading,
      orientationScan: props.orientationScan,
      torchOn: props.torchOn,
    },
  }),
]);
/**
 * Contiene cual el elemento seleccionado del array de confUCMultiSelector.
 * @type {Ref<typeof confUCMultiSelector['value'][number] | undefined>}
 */
const currentConfMultiSelector = ref(undefined);

/**
 * @public
 * @returns {void}
 */
function reset() {
  allowChangeComponentUCMultiInputSelector.value = false; // Bloqueamos que se pueda regresar a la lectura de caja sin leer actividad.
  multiInputSelectorRef.value?.selectEntry('PALET', false);
  currentConfMultiSelector.value = confUCMultiSelector.value[0];
}

/**
 * @public
 * @returns {Promise<void>}
 */
async function focus() {
  await sleep(100);
  multiInputSelectorRef.value?.focus();
}

/**
 * @param {typeof confUCMultiSelector['value'][number]} value
 * @returns {void}
 */
function handleInputChange(value) {
  /* if (currentConfMultiSelector.value.id === 'PALET') {
        this.Reset();
      } */
  currentConfMultiSelector.value = value;
}

/**
 * @param {string} val
 * @returns {void}
 */
function handleValidateInput(val) {
  if (!currentConfMultiSelector.value) {
    return;
  }

  if (currentConfMultiSelector.value.id === 'BOX') {
    emit('code-reading-elaborated-article', val);
  } else if (currentConfMultiSelector.value.id === 'PALET') {
    emit('code-reading-palet', val);
  }
}

function codeScannerClose() {
  if (props.continuousBarCodeReading && currentConfMultiSelector.value?.id === 'BOX') {
    emit('updateFront');
  }
}

/**
 * @public
 * @returns {void}
 */
function changeInputToPalet() {
  multiInputSelectorRef.value?.selectEntry('PALET', true);
}

/**
 * @public
 * @returns {void}
 */
function changeInputToElaboratedArticle() {
  multiInputSelectorRef.value?.selectEntry('BOX', true);
}

onMounted(() => {
  currentConfMultiSelector.value = confUCMultiSelector.value[0];
});

defineExpose({
  changeInputToElaboratedArticle,
  changeInputToPalet,
  focus,
  reset,
});
</script>

<style lang="scss">
.main-component-pc-lector-articulo-elaborado-y-actividad {
  .header {
    display: flex;
    justify-content: space-between; // Esto coloca los elementos al inicio y al final
    color: #24253d;
    text-transform: none;
    font-size: 17px;
    padding-left: 25px;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-right: 12px;
    border-bottom: 4px;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    border-color: var(--his-disabled);
    border-style: solid;
    background: aliceblue;

    .header-lefth {
      padding-top: 3px;
      color: var(--his-steel-blue);
      font-size: 14px;
      font-weight: 400;

      .barCode {
        font-weight: 600;
      }
    }
  }
}
</style>
